import React from "react";
import { FileText, X } from "lucide-react";

import StatusPill from "../StatusPill";
import type { StatusTone } from "../../utils/statusPill";
import type { ChangedChoice, ReviewItem } from "../../utils/addFilesReview";
import { formatFileSize } from "../../utils/formatUtils";

interface FileReviewRowProps {
	item: ReviewItem;
	choice: ChangedChoice;
	onChoose: (path: string, choice: ChangedChoice) => void;
	onRemove: (path: string) => void;
}

const PILLS: Record<ReviewItem["status"], { tone: StatusTone; label: string }> = {
	new: { tone: "green", label: "New" },
	changed: { tone: "amber", label: "Updated version" },
	duplicate: { tone: "grey", label: "Already here" },
	checking: { tone: "grey", label: "Checking" },
};

const CHOICES: Array<{ value: ChangedChoice; label: string; hint: string }> = [
	{
		value: "replace",
		label: "Use as the latest version",
		hint: "Replaces the copy in this deal.",
	},
	{
		value: "keep-both",
		label: "Keep both",
		hint: "Saved next to it, with (2) added to the name.",
	},
	{
		value: "skip",
		label: "Skip this file",
		hint: "The copy in this deal stays as it is.",
	},
];

const addedOn = (iso: string): string => {
	const date = new Date(iso);
	return Number.isNaN(date.getTime())
		? ""
		: `, added ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};

const describe = (item: ReviewItem): string => {
	switch (item.status) {
		case "changed":
			return `This deal already has a file with this name${addedOn(item.existing?.created_at ?? "")}. This one is different.`;
		case "duplicate":
			return "Exactly the same as the file already in this deal. It will be left out.";
		case "checking":
			return "Comparing with the copy already in this deal…";
		default:
			return "";
	}
};

/** One incoming file in the add-files review, and what will happen to it. */
const FileReviewRow: React.FC<FileReviewRowProps> = ({
	item,
	choice,
	onChoose,
	onRemove,
}) => {
	const pill = PILLS[item.status];
	const detail = describe(item);
	const activeHint = CHOICES.find((option) => option.value === choice)?.hint;

	return (
		<li
			className={`flex items-start gap-3 border-b border-gray-100 py-3 last:border-b-0 ${
				item.status === "duplicate" ? "opacity-70" : ""
			}`}
		>
			<FileText
				className="mt-0.5 h-5 w-5 flex-shrink-0 text-text-muted"
				aria-hidden="true"
			/>
			<div className="min-w-0 flex-1">
				<p className="truncate text-base font-medium text-text-strong" title={item.path}>
					{item.path}
				</p>
				<p className="mt-0.5 text-xs text-text-muted">
					{formatFileSize(item.file.size)}
					{detail ? ` · ${detail}` : ""}
				</p>
				{item.status === "changed" && (
					<>
						<div
							role="radiogroup"
							aria-label={`What to do with ${item.path}`}
							className="mt-2 inline-flex flex-wrap gap-0.5 rounded-md bg-gray-100 p-0.5"
						>
							{CHOICES.map((option) => {
								const selected = option.value === choice;
								return (
									<button
										key={option.value}
										type="button"
										role="radio"
										aria-checked={selected}
										onClick={() => onChoose(item.path, option.value)}
										className={`rounded px-3 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
											selected
												? "bg-white font-medium text-text-base shadow-sm"
												: "text-text-muted hover:text-text-base"
										}`}
									>
										{option.label}
									</button>
								);
							})}
						</div>
						{activeHint && (
							<p className="mt-1.5 text-xs text-text-muted">{activeHint}</p>
						)}
					</>
				)}
			</div>
			<StatusPill tone={pill.tone} label={pill.label} />
			<button
				type="button"
				onClick={() => onRemove(item.path)}
				aria-label={`Remove ${item.path}`}
				className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
			>
				<X className="h-4 w-4" aria-hidden="true" />
			</button>
		</li>
	);
};

export default React.memo(FileReviewRow);
